import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { useAuth } from "@/lib/auth";
import { Lock, ChevronLeft, ChevronRight, X } from "lucide-react";

const CATS = [
  { key: "all", label: "Wszystko" },
  { key: "zloty", label: "Zloty" },
  { key: "wyprawy", label: "Wyprawy" },
  { key: "inne", label: "Inne" },
];

function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Gallery() {
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showPrivate, setShowPrivate] = useState(false);
  const [openAlbum, setOpenAlbum] = useState(null); // album object
  const [photos, setPhotos] = useState([]); // photos of openAlbum
  const [lightbox, setLightbox] = useState(null); // index in photos

  useEffect(() => {
    if (user === null) return;
    api.get("/albums").then((r) => Array.isArray(r.data) && setAlbums(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!openAlbum) { setPhotos([]); return; }
    api.get(`/gallery?album_id=${openAlbum.id}`)
      .then((r) => Array.isArray(r.data) && setPhotos(r.data))
      .catch(() => {});
  }, [openAlbum]);

  const filtered = useMemo(
    () => albums.filter((a) => {
      if (filter !== "all" && (a.category || "wyprawy") !== filter) return false;
      if (showPrivate && a.is_public) return false;
      return true;
    }),
    [albums, filter, showPrivate]
  );

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i == null ? null : (i - 1 + photos.length) % photos.length)), [photos.length]);
  const next = useCallback(() => setLightbox((i) => (i == null ? null : (i + 1) % photos.length)), [photos.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const h = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightbox, closeLightbox, prev, next]);

  // ====== INSIDE-ALBUM VIEW ======
  if (openAlbum) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-16" data-testid="album-view">
        <button onClick={() => setOpenAlbum(null)} className="btn-ghost mb-6" data-testid="album-back">
          ← Wróć do albumów
        </button>
        <SectionHeader title={openAlbum.title} />
        {openAlbum.description && (
          <p className="text-center mt-4" style={{ color: "var(--sr-muted)" }}>{openAlbum.description}</p>
        )}
        <p className="text-center mt-2 text-xs font-nav uppercase tracking-widest" style={{ color: "var(--sr-fire-2)" }}>
          {photos.length} {photos.length === 1 ? "zdjęcie" : photos.length < 5 ? "zdjęcia" : "zdjęć"} · {openAlbum.category}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
          {photos.map((g, i) => (
            <button
              key={g.id}
              onClick={() => setLightbox(i)}
              className="gallery-img fade-up text-left"
              style={{ aspectRatio: "1/1", animationDelay: `${(i % 8) * 60}ms` }}
              data-testid={`photo-${i}`}
            >
              <img src={resolveImg(g.image_url)} alt={g.caption || "Zdjęcie"} />
            </button>
          ))}
          {photos.length === 0 && (
            <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>Brak zdjęć w tym albumie.</p>
          )}
        </div>

        {lightbox != null && photos[lightbox] && (
          <div className="modal-bg" onClick={closeLightbox} data-testid="lightbox">
            <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={resolveImg(photos[lightbox].image_url)} alt={photos[lightbox].caption || ""} className="w-full h-auto max-h-[80vh] object-contain" />
              {photos[lightbox].caption && (
                <p className="text-center mt-3 font-nav uppercase tracking-widest text-sm" style={{ color: "var(--sr-cream-soft)" }}>
                  {photos[lightbox].caption}
                </p>
              )}
              <p className="text-center mt-1 text-xs" style={{ color: "var(--sr-muted)" }}>
                {lightbox + 1} / {photos.length}
              </p>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-[var(--sr-fire)]" data-testid="lightbox-prev" aria-label="Poprzednie">
                <ChevronLeft size={28} />
              </button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-[var(--sr-fire)]" data-testid="lightbox-next" aria-label="Następne">
                <ChevronRight size={28} />
              </button>
              <button onClick={closeLightbox} className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white hover:bg-[var(--sr-fire)]" data-testid="lightbox-close" aria-label="Zamknij">
                <X size={22} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ====== ALBUMS LIST VIEW ======
  return (
    <div className="max-w-7xl mx-auto px-5 py-16" data-testid="gallery-page">
      <SectionHeader title="Galeria" />
      <p className="text-center mt-6" style={{ color: "var(--sr-muted)" }}>
        {user ? "Pełna galeria klubowa — w tym albumy tylko dla członków." : "Wybrane albumy z naszych wypraw i zlotów."}
      </p>
      {!user && (
        <div className="flex items-center justify-center gap-2 mt-3 text-sm" style={{ color: "var(--sr-fire)" }}>
          <Lock size={14} /> Zaloguj się, by zobaczyć albumy wewnętrzne.
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-10" data-testid="gallery-filters">
        {CATS.map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            className={filter === c.key ? "btn-solid" : "btn-ghost"}
            data-testid={`filter-${c.key}`}
          >
            {c.label}
          </button>
        ))}
        {user && (
          <button
            onClick={() => setShowPrivate(!showPrivate)}
            className={showPrivate ? "btn-solid" : "btn-ghost"}
            data-testid="filter-private"
          >
            🔒 Tylko klubowe
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        {filtered.map((a, i) => (
          <button
            key={a.id}
            onClick={() => setOpenAlbum(a)}
            className="sr-card overflow-hidden group text-left fade-up"
            style={{ animationDelay: `${(i % 6) * 80}ms` }}
            data-testid={`album-${a.id}`}
          >
            <div className="relative" style={{ aspectRatio: "4/3", background: "rgba(0,0,0,0.4)" }}>
              {a.cover_url ? (
                <img
                  src={resolveImg(a.cover_url)}
                  alt={a.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-nav uppercase text-xs tracking-widest" style={{ color: "var(--sr-muted)" }}>
                  Brak okładki
                </div>
              )}
              {!a.is_public && (
                <span className="absolute top-2 right-2 text-xs px-2 py-1 font-nav uppercase tracking-widest" style={{ background: "rgba(255,106,26,0.9)", color: "#0c0a09" }}>
                  Klub
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 p-4" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }}>
                <h3 className="font-display text-lg md:text-xl text-[var(--sr-cream)] group-hover:text-[var(--sr-fire)] transition-colors">{a.title}</h3>
                <p className="text-xs font-nav uppercase tracking-widest mt-1" style={{ color: "var(--sr-fire-2)" }}>
                  {a.category} · {a.photo_count} {a.photo_count === 1 ? "zdjęcie" : a.photo_count < 5 ? "zdjęcia" : "zdjęć"}
                </p>
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-center" style={{ color: "var(--sr-muted)" }}>
            Brak albumów w tej kategorii.
          </p>
        )}
      </div>
    </div>
  );
}
